// The transaction's revision chain from `GET /api/transactions/{id}`
// (M1 plan §5.1 point 5), oldest first: amount, merchant string and
// removal reason at each step. Renders nothing when there is only the
// current revision -- an amendment trail is not fabricated for a
// transaction that was never amended.
import { AmountText } from '@/components/AmountText';
import type { TransactionRevision } from '@/api/hooks/useTransaction';

function removalLabel(reason: Exclude<TransactionRevision['removal_reason'], null>): string {
  switch (reason) {
    case 'superseded_by_posting': return 'Replaced by posted transaction';
    case 'provider_modified': return 'Changed by bank';
    case 'provider_removed': return 'Removed by bank';
    default: {
      const exhaustive: never = reason;
      return exhaustive;
    }
  }
}

export function AmendmentTrail({ revisions, currency }: { revisions: TransactionRevision[]; currency: string }) {
  if (revisions.length <= 1) return null;
  return (
    <div data-testid="amendment-trail" className="mt-3 text-xs text-muted-foreground">
      <div className="font-semibold text-foreground">Amendment history</div>
      <ul className="mt-1 space-y-1">
        {revisions.map((revision) => (
          <li key={revision.revision} className="flex items-center justify-between gap-2">
            <span>
              Rev {revision.revision} · {revision.merchant_raw}
              {revision.removal_reason ? ` · ${removalLabel(revision.removal_reason)}` : ''}
            </span>
            <AmountText value={revision.amount} currency={currency} className="text-xs" />
          </li>
        ))}
      </ul>
    </div>
  );
}

export default AmendmentTrail;
